/**
 * Give an uploaded file a name that cannot collide with an existing document.
 *
 * `document_files.file_original_name` carries a tenant-GLOBAL unique constraint and the API
 * recognises re-imported documents by that column. An upload whose name matches an existing
 * document therefore does not create a new record — it takes over the existing one and pulls
 * it into the uploader's container (AAR-1714, and the 409s analysed in AAR-1090).
 *
 * The client side avoids the situation entirely by making every uploaded name unique. Do not
 * rely on the server's de-duplication: it only appends a second-granularity timestamp, so two
 * uploads within the same second still collide.
 */
let uploadCounter = 0

export const makeUniqueBinary = (binary: File | Blob): File => {
	const file = binary instanceof File ? binary : new File([binary], 'upload')
	const dotIndex = file.name.lastIndexOf('.')
	const name = dotIndex > -1 ? file.name.substring(0, dotIndex) : file.name
	const ext = dotIndex > -1 ? file.name.substring(dotIndex) : ''
	// Der Zeitstempel allein reicht nicht: beim Mehrfach-Upload laeuft die Zuordnung
	// synchron ueber alle ausgewaehlten Dateien, Date.now() ist dann fuer alle gleich.
	// Zwei gleich benannte Dateien -- oder zwei, die sich nur in der Gross-/Kleinschreibung
	// unterscheiden, denn der Unique Key ist case-insensitiv -- bekaemen denselben Namen und
	// die zweite wuerde die erste ueberschreiben. Der Zaehler macht sie innerhalb der Seite
	// eindeutig, das Zufallssuffix ueber Geraete hinweg.
	const discriminator = `${(++uploadCounter).toString(36)}${Math.random().toString(36).slice(2, 6)}`
	const uniqueName = `${name}_${Date.now()}_${discriminator}${ext}`
	return new File([file], uniqueName, { type: file.type })
}

/**
 * Give a ZIP entry a name that does not collide with an entry already in the archive.
 *
 * JSZip keys its entries by name: adding a second entry under an existing name replaces the
 * first one silently. Beim Download mehrerer gleichnamiger Dokumente landete deshalb nur das
 * letzte im Archiv (AAR-192).
 *
 * Anders als beim Upload zaehlt hier die Lesbarkeit, nicht die globale Eindeutigkeit — der
 * Name bekommt daher ein schlichtes "(2)" statt eines Zeitstempels.
 */
export const makeUniqueZipEntryName = (
	name: string,
	isTaken: (candidate: string) => boolean
): string => {
	if (!isTaken(name)) return name

	const dotIndex = name.lastIndexOf('.')
	const base = dotIndex > 0 ? name.substring(0, dotIndex) : name
	const ext = dotIndex > 0 ? name.substring(dotIndex) : ''

	let counter = 2
	while (isTaken(`${base} (${counter})${ext}`)) counter++

	return `${base} (${counter})${ext}`
}
